import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';

/**
 * Kill Switch - Componente per bloccare l'accesso all'app da remoto
 * 
 * Come usare:
 * 1. Crea un file "app-status.json" nella root del tuo hosting con il contenuto:
 *    { "enabled": true }  -> App funzionante
 *    { "enabled": false, "message": "App in manutenzione" } -> App bloccata
 * 
 * 2. Quando vuoi bloccare l'app, modifica il file a:
 *    { "enabled": false, "message": "Tuo messaggio qui" }
 */
const KillSwitch = ({ children }) => {
    const [blocked, setBlocked] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkAppStatus = async () => {
            try {
                // Prova a caricare il file di stato
                const response = await fetch('/app-status.json', {
                    cache: 'no-store',
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    }
                });

                if (response.ok) {
                    const status = await response.json();

                    if (status.enabled === false) {
                        setBlocked(true);
                        setMessage(status.message || 'App temporaneamente non disponibile');
                    }
                }
                // Se il file non esiste (404), l'app funziona normalmente
            } catch (error) {
                // In caso di errore (es. file non esiste), l'app funziona normalmente
                console.log('Kill switch check: nessun blocco attivo');
            } finally {
                setLoading(false);
            }
        };

        checkAppStatus();

        // Ricontrolla ogni 5 minuti
        const interval = setInterval(checkAppStatus, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    // Mostra loading iniziale
    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <Text style={styles.loadingText}>Caricamento...</Text>
            </View>
        );
    }

    // Mostra schermata di blocco
    if (blocked) {
        return (
            <View style={styles.blockedContainer}>
                <View style={styles.blockedContent}>
                    <Text style={styles.blockedIcon}>🚧</Text>
                    <Text style={styles.blockedTitle}>App Non Disponibile</Text>
                    <Text style={styles.blockedMessage}>{message}</Text>
                    <Text style={styles.blockedHint}>Riprova più tardi</Text>
                </View>
            </View>
        );
    }

    // App funziona normalmente
    return children;
};

const styles = StyleSheet.create({
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
    },
    loadingText: {
        fontSize: 18,
        color: '#666',
    },
    blockedContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        padding: 20,
    },
    blockedContent: {
        backgroundColor: '#fff',
        borderRadius: 16,
        padding: 40,
        alignItems: 'center',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 4,
    },
    blockedIcon: {
        fontSize: 64,
        marginBottom: 20,
    },
    blockedTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#d32f2f',
        marginBottom: 16,
        textAlign: 'center',
    },
    blockedMessage: {
        fontSize: 16,
        color: '#666',
        textAlign: 'center',
        lineHeight: 24,
        marginBottom: 20,
    },
    blockedHint: {
        fontSize: 14,
        color: '#999',
        fontStyle: 'italic',
    },
});

export default KillSwitch;
